/**
 * Jobs table component
 * Full jobs list with sorting, filtering, and pagination
 */

import { useState } from 'react';
import { Table, Space, Button, Tooltip, Dropdown, Typography, Tag } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { SorterResult, FilterValue } from 'antd/es/table/interface';
import {
  EyeOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  MoreOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { JobStatusBadge } from '@/components/common/StatusBadge';
import { JobProgressBar } from './JobProgressBar';
import type { Job, JobStatus } from '@/types/job';
import { formatNumber, formatRelativeTime, formatDuration } from '@/utils/formatters';
import { JOB_STATUS_CONFIG } from '@/utils/constants';

const { Link } = Typography;

interface JobsTableProps {
  jobs: Job[];
  loading?: boolean;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  selectedRowKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  onDelete?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onRetry?: (id: string) => void;
  onExport?: (id: string) => void;
}

export function JobsTable({
  jobs,
  loading,
  pagination,
  selectedRowKeys = [],
  onSelectionChange,
  onDelete,
  onPause,
  onResume,
  onRetry,
  onExport,
}: JobsTableProps) {
  const navigate = useNavigate();
  const [sortedInfo, setSortedInfo] = useState<SorterResult<Job>>({});

  const handleTableChange: TableProps<Job>['onChange'] = (_, __, sorter) => {
    setSortedInfo(sorter as SorterResult<Job>);
  };

  const getActionMenuItems = (record: Job) => {
    const items = [];

    items.push({
      key: 'view',
      icon: <EyeOutlined />,
      label: 'View Details',
      onClick: () => navigate(`/jobs/${record.id}`),
    });

    if (record.status === 'running') {
      items.push({
        key: 'pause',
        icon: <PauseCircleOutlined />,
        label: 'Pause Job',
        onClick: () => onPause?.(record.id),
      });
    }

    if (record.status === 'paused') {
      items.push({
        key: 'resume',
        icon: <PlayCircleOutlined />,
        label: 'Resume Job',
        onClick: () => onResume?.(record.id),
      });
    }

    if (record.status === 'failed') {
      items.push({
        key: 'retry',
        icon: <ReloadOutlined />,
        label: 'Retry Failed Cells',
        onClick: () => onRetry?.(record.id),
      });
    }

    if (record.status === 'completed' && record.placesFound > 0) {
      items.push({
        key: 'export',
        icon: <DownloadOutlined />,
        label: 'Export Results',
        onClick: () => onExport?.(record.id),
      });
    }

    items.push({ type: 'divider' as const });

    items.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete Job',
      danger: true,
      onClick: () => onDelete?.(record.id),
    });

    return items;
  };

  const columns: ColumnsType<Job> = [
    {
      title: 'Query',
      dataIndex: 'searchQuery',
      key: 'searchQuery',
      sorter: (a, b) => a.searchQuery.localeCompare(b.searchQuery),
      sortOrder: sortedInfo.columnKey === 'searchQuery' ? sortedInfo.order : null,
      render: (query: string, record) => (
        <div>
          <Link
            onClick={() => navigate(`/jobs/${record.id}`)}
            style={{ fontWeight: 500 }}
          >
            {query}
          </Link>
          {record.name && (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {record.name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const locationText = record.locationDisplay || record.locationName;
        const { bounds } = record;

        if (!locationText && !bounds) return '-';

        const boundsTooltip = bounds
          ? `Bounds: ${bounds.north.toFixed(4)}°N, ${bounds.south.toFixed(4)}°S, ${bounds.east.toFixed(4)}°E, ${bounds.west.toFixed(4)}°W`
          : '';

        return (
          <Tooltip title={boundsTooltip}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {locationText || 'Custom Area'}
              </span>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                {record.totalCells} cells
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: Object.entries(JOB_STATUS_CONFIG).map(([key, config]) => ({
        text: config.label,
        value: key,
      })),
      onFilter: (value, record) => record.status === value,
      render: (status: JobStatus) => <JobStatusBadge status={status} size="small" />,
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 180,
      sorter: (a, b) => {
        const progA = a.totalCells > 0 ? a.completedCells / a.totalCells : 0;
        const progB = b.totalCells > 0 ? b.completedCells / b.totalCells : 0;
        return progA - progB;
      },
      sortOrder: sortedInfo.columnKey === 'progress' ? sortedInfo.order : null,
      render: (_, record) => (
        <JobProgressBar
          completed={record.completedCells}
          failed={record.failedCells}
          total={record.totalCells}
          status={record.status}
          showText
          size="small"
        />
      ),
    },
    {
      title: 'Places',
      dataIndex: 'placesFound',
      key: 'placesFound',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.placesFound - b.placesFound,
      sortOrder: sortedInfo.columnKey === 'placesFound' ? sortedInfo.order : null,
      render: (places: number) => (
        <span style={{ fontWeight: places > 0 ? 500 : 400 }}>
          {formatNumber(places)}
        </span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      sortOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
      defaultSortOrder: 'descend',
      render: (date: string) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <span style={{ fontSize: 13 }}>{formatRelativeTime(date)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Est. Remaining',
      key: 'duration',
      width: 120,
      render: (_, record) => {
        // Completed jobs - show total duration
        if (record.status === 'completed' && record.completedAt && record.startedAt) {
          const duration = new Date(record.completedAt).getTime() - new Date(record.startedAt).getTime();
          return (
            <Tooltip title="Total duration">
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{formatDuration(duration)}</span>
            </Tooltip>
          );
        }

        // Running jobs - estimate remaining time
        if ((record.status === 'running' || record.status === 'paused') && record.startedAt && record.totalCells > 0) {
          const elapsed = Date.now() - new Date(record.startedAt).getTime();
          const progress = record.completedCells / record.totalCells;

          if (progress > 0) {
            const estimatedTotal = elapsed / progress;
            const remaining = estimatedTotal - elapsed;

            if (remaining > 0) {
              return (
                <Tooltip title={`${Math.round(progress * 100)}% complete`}>
                  <Tag color="processing" style={{ margin: 0 }}>
                    ~{formatDuration(remaining)}
                  </Tag>
                </Tooltip>
              );
            }
          }

          return (
            <Tag color="processing" style={{ margin: 0 }}>
              Calculating...
            </Tag>
          );
        }

        // Pending or other
        return <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/jobs/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={jobs}
      rowKey="id"
      loading={loading}
      onChange={handleTableChange}
      pagination={
        pagination
          ? {
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: pagination.onChange,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
            }
          : false
      }
      rowSelection={
        onSelectionChange
          ? {
              selectedRowKeys,
              onChange: (keys) => onSelectionChange(keys as string[]),
            }
          : undefined
      }
      scroll={{ x: 1000 }}
      locale={{ emptyText: 'No jobs found' }}
    />
  );
}

export default JobsTable;
